import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { renderAsync } from 'docx-preview';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';
import { replaceVariablesInHTML } from '../utils/docxParser';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AttachmentModal from '../components/AttachmentModal';
import AICompareModal from '../components/AICompareModal';
import AuthorizedImage from '../components/AuthorizedImage';
import { appendAttachmentsToZip } from '../utils/docxAttachmentAppender';
import { API_BASE_URL } from '../config';

// Helper function to handle document download/view
const handleDocumentAction = async (filePath: string, fileName: string, isPdf: boolean) => {
  const token = localStorage.getItem('access_token');

  if (!token) {
    alert('Anda harus login untuk mengakses dokumen');
    return;
  }

  // Extract just the filename from path (remove 'uploads/documents/' prefix if exists)
  let cleanPath = filePath;
  if (cleanPath.startsWith('uploads/documents/')) {
    cleanPath = cleanPath.replace('uploads/documents/', '');
  }

  const url = `${API_BASE_URL}/api/documents/download/${cleanPath}`;

  try {
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        alert('Sesi Anda telah berakhir. Silakan login kembali.');
        return;
      }
      throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    if (isPdf) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 2000);
    } else {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    }
  } catch (error) {
    console.error('Error handling document:', error);
    alert('Gagal membuka/mengunduh dokumen. Silakan coba lagi.');
  }
};

const TaskDraftingV2: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { darkMode } = useTheme();

  const [task, setTask] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [agreementTypes, setAgreementTypes] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [template, setTemplate] = useState<any>(null);
  const [variables, setVariables] = useState<any[]>([]);
  const [userGroups, setUserGroups] = useState<number[]>([]);
  const [supportingDocuments, setSupportingDocuments] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Document preview states
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const hiddenRenderContainerRef = useRef<HTMLDivElement>(null);
  const [fileArrayBuffer, setFileArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [templateBlob, setTemplateBlob] = useState<Blob | null>(null);
  const [documentHtml, setDocumentHtml] = useState<string>('');
  const [editorContent, setEditorContent] = useState<string>('');
  const [leftPanelWidth, setLeftPanelWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [variablesExpanded, setVariablesExpanded] = useState(true);
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [previewTrigger, setPreviewTrigger] = useState(0); // Trigger untuk force update preview

  // Attachments states
  interface Attachment {
    id: string | number;
    title: string;
    imageUrl: string;
    imageFile?: File;
    description: string;
    order: number;
    file_path?: string;
  }

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState<Attachment | null>(null);
  const [attachmentForm, setAttachmentForm] = useState({
    title: '',
    description: '',
    imageFile: null as File | null,
    imagePreview: '' as string
  });
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  // AI Comparison State
  const [showCompareModal, setShowCompareModal] = useState(false);

  // Ref to track if initial data has been loaded (to prevent multiple fetches)
  const hasLoadedInitialData = useRef(false);
  // Ref to track if initial preview has been rendered
  const hasRenderedInitialPreview = useRef(false);

  // Reset refs when task ID changes
  useEffect(() => {
    hasLoadedInitialData.current = false;
    hasRenderedInitialPreview.current = false;
  }, [id]);

  // Helper untuk membersihkan variable name dari karakter khusus
  const cleanVariableName = (name: string): string => {
    return name.replace(/{|}/g, '').trim();
  };

  // Helper untuk mendapatkan form field key dari variable name
  const getVariableFormKey = (variableName: string): string => {
    return `variable_${cleanVariableName(variableName)}`;
  };

  // Helper untuk check apakah variable harus diisi oleh user ini
  const isVariableForCurrentUser = (variable: any): boolean => {
    if (!variable.group_id) return false;
    if (!userGroups.length) return false;
    return userGroups.includes(variable.group_id);
  };

  // Zod schema untuk form validation
  const createFormSchema = (requiredVariables: any[]) => {
    const schemaObject: any = {
      title: z.string().min(1, 'Title wajib diisi'),
      description: z.string().optional(),
      categoryId: z.string().min(1, 'Category wajib dipilih'),
      agreementTypeId: z.string().min(1, 'Agreement Type wajib dipilih'),
      agreementNo: z.string().optional(),
    };

    // Add validation for required variables
    requiredVariables.forEach((variable) => {
      const cleanName = cleanVariableName(variable.name);
      schemaObject[`variable_${cleanName}`] = z.string().min(1, `${cleanName} wajib diisi`);
    });

    return z.object(schemaObject);
  };

  // Initialize form dengan React Hook Form
  // Create schema dynamically based on required variables
  const requiredVariables = variables.filter((v: any) => isVariableForCurrentUser(v));
  const formSchema = createFormSchema(requiredVariables);

  const { register, handleSubmit, control, formState: { errors }, watch, setValue, reset, getValues } = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
  });

  // Load task data and initialize form
  useEffect(() => {
    const loadTaskData = async () => {
      // Only load data once on initial mount
      if (hasLoadedInitialData.current) {
        return;
      }

      if (!id) {
        setError('Task ID tidak ditemukan');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Load task data
        const taskData = await apiService.getTask(Number(id));
        setTask(taskData);

        // Load supporting documents from task
        if (taskData.supporting_documents && Array.isArray(taskData.supporting_documents)) {
          setSupportingDocuments(taskData.supporting_documents);
        }

        // Load categories, agreement types, companies, users, dan user groups secara parallel
        const [cats, types, comps, usersData, userGroupsData] = await Promise.all([
          apiService.getCategories(),
          apiService.getAgreementTypes(),
          apiService.getCompanies(),
          apiService.getUsers(),
          user?.id ? apiService.getUserGroups(user.id) : Promise.resolve([])
        ]);

        setCategories(cats);
        setAgreementTypes(types);
        setCompanies(comps.filter((c: any) => c.is_active !== false));
        setUsers(usersData.filter((u: any) => u.is_active !== false));

        // Set user groups
        if (userGroupsData && userGroupsData.length > 0) {
          const groupIds = userGroupsData.map((ug: any) => ug.group_id);
          setUserGroups(groupIds);
        }

        // Set form data dari task
        const formValues: any = {
          title: taskData.title || '',
          description: taskData.description || '',
          categoryId: taskData.category_id ? String(taskData.category_id) : '',
          agreementTypeId: taskData.agreement_type_id ? String(taskData.agreement_type_id) : '',
          companyId: taskData.company_id ? String(taskData.company_id) : '',
          agreementNo: taskData.agreement_no || ''
        };

        // Load template jika ada
        if (taskData.template_id) {
          try {
            console.log('Loading template with ID:', taskData.template_id);
            const fullTemplate = await apiService.getTemplate(taskData.template_id);
            console.log('Template loaded:', fullTemplate);
            setTemplate(fullTemplate);

            // Initialize variables dari task (gunakan value yang sudah ada jika ada)
            if (fullTemplate && fullTemplate.variables) {
              const taskVariables = taskData.variables || [];
              const initializedVariables = fullTemplate.variables.map((v: any) => {
                const existingVar = taskVariables.find((tv: any) => tv.name === v.name);
                const varValue = existingVar?.value || '';
                const formKey = getVariableFormKey(v.name);
                formValues[formKey] = varValue;
                return {
                  ...v,
                  value: varValue
                };
              });
              setVariables(initializedVariables);
            }

            // Load template file untuk preview
            console.log('Loading template file for template ID:', fullTemplate.id);
            await loadTemplateFile(fullTemplate.id);
          } catch (err: any) {
            console.error('Error loading template:', err);
            setError(err.message || 'Gagal memuat template');
          }
        } else if (taskData.category_id && taskData.agreement_type_id) {
          try {
            const templateData = await apiService.checkDuplicateTemplate(
              taskData.category_id,
              taskData.agreement_type_id
            );

            if (templateData) {
              console.log('Loading template from category/agreement type, template ID:', templateData.id);
              const fullTemplate = await apiService.getTemplate(templateData.id);
              console.log('Template loaded:', fullTemplate);
              setTemplate(fullTemplate);

              if (fullTemplate && fullTemplate.variables) {
                const taskVariables = taskData.variables || [];
                const initializedVariables = fullTemplate.variables.map((v: any) => {
                  const existingVar = taskVariables.find((tv: any) => tv.name === v.name);
                  const varValue = existingVar?.value || '';
                  const formKey = getVariableFormKey(v.name);
                  formValues[formKey] = varValue;
                  return {
                    ...v,
                    value: varValue
                  };
                });
                setVariables(initializedVariables);
              }

              console.log('Loading template file for template ID:', fullTemplate.id);
              await loadTemplateFile(fullTemplate.id);
            } else {
              console.warn('No template found for category and agreement type');
            }
          } catch (err: any) {
            console.error('Error loading template:', err);
          }
        }

        // Reset form dengan values
        reset(formValues);

        // Mark that initial data has been loaded
        hasLoadedInitialData.current = true;

      } catch (err: any) {
        console.error('Error loading task data:', err);
        setError('Gagal memuat data task. Silakan refresh halaman.');
      } finally {
        setLoading(false);
      }
    };

    loadTaskData();
  }, [id, user?.id, reset]);

  // Load assignments when task is loaded
  useEffect(() => {
    const loadAssignments = async () => {
      if (!id) return;

      setLoadingAssignments(true);
      try {
        const assignmentsData = await apiService.getTaskAssignments(Number(id));
        setAssignments(assignmentsData);
      } catch (err: any) {
        console.error('Error loading assignments:', err);
      } finally {
        setLoadingAssignments(false);
      }
    };

    if (task) {
      loadAssignments();
    }
  }, [id, task]);

  // Load attachments when task is loaded
  useEffect(() => {
    const loadAttachments = async () => {
      if (!id) return;

      setLoadingAttachments(true);
      try {
        const attachmentsData = await apiService.getTaskAttachments(Number(id));
        // Convert API response to component format
        const formattedAttachments: Attachment[] = attachmentsData.map((att: any) => ({
          id: att.id,
          title: att.title,
          imageUrl: apiService.getAttachmentImageUrl(att.id), // Use attachment ID for image URL
          description: att.description || '',
          order: att.order,
          file_path: att.file_path
        }));
        setAttachments(formattedAttachments);
      } catch (err: any) {
        console.error('Error loading attachments:', err);
      } finally {
        setLoadingAttachments(false);
      }
    };

    if (task) {
      loadAttachments();
    }
  }, [id, task]);

  // Load template file dari backend
  const loadTemplateFile = async (templateId: number) => {
    try {
      const token = localStorage.getItem('access_token');

      console.log(`Fetching template file from: ${API_BASE_URL}/api/templates/${templateId}/file`);

      const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}/file?t=${new Date().getTime()}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      console.log('Template file response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
        } catch (e) {
          errorText = 'Could not read error response';
        }
        console.error('Failed to load template file:', response.status, errorText);
        throw new Error(`Gagal memuat file template: ${response.status} ${response.statusText}. ${errorText}`);
      }

      // Get blob first, then convert to arrayBuffer
      console.log('Converting response to blob...');
      const blob = await response.blob();
      console.log('Blob created, size:', blob.size, 'type:', blob.type);
      const arrayBuffer = await blob.arrayBuffer();
      console.log('ArrayBuffer created, size:', arrayBuffer.byteLength);

      setFileArrayBuffer(arrayBuffer);
      setTemplateBlob(blob);
      console.log('Template file loaded successfully');
    } catch (err: any) {
      console.error('Error loading template file:', err);
      const errorMessage = err.message || 'Gagal memuat file template';
      setError(errorMessage);
      throw err; // Re-throw untuk ditangani di caller
    }
  };

  // Initial load document HTML (hanya sekali saat fileArrayBuffer pertama kali di-load)
  useEffect(() => {
    if (!fileArrayBuffer || documentHtml) {
      return;
    }

    let isMounted = true;

    const loadDocument = async () => {
      let retryCount = 0;
      const maxRetries = 20;

      const checkAndLoad = async () => {
        if (!isMounted) {
          return;
        }

        const renderContainer = hiddenRenderContainerRef.current;

        if (!renderContainer) {
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(checkAndLoad, 100);
            return;
          } else {
            console.error('Container tidak ditemukan setelah beberapa retry');
            return;
          }
        }

        try {
          if (renderContainer) {
            renderContainer.innerHTML = '';
          }

          // Render document to hidden container
          await renderAsync(
            fileArrayBuffer,
            renderContainer,
            undefined,
            {
              className: 'docx-wrapper',
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: true,
              experimental: false,
              trimXmlDeclaration: true,
              debug: false
            }
          );

          // Wait a bit for renderAsync to finish all DOM manipulations
          await new Promise(resolve => setTimeout(resolve, 100));

          // Get initial HTML (without variable replacement yet)
          if (renderContainer && isMounted) {
            try {
              const htmlContent = renderContainer.innerHTML;
              setDocumentHtml(htmlContent);
              setEditorContent(htmlContent);
            } catch (err) {
              console.warn('Error setting initial document HTML:', err);
            }
          }
        } catch (err) {
          console.error('Error loading document:', err);
          setError('Gagal memuat dokumen');
        }
      };

      setTimeout(checkAndLoad, 200);
    };

    loadDocument();

    return () => {
      isMounted = false;
    };
  }, [fileArrayBuffer]);


  // NOTE: Removed watch() to prevent re-render on every keystroke
  // Use getValues() inside functions when needed instead

  // Auto-render preview dengan variabel yang sudah ada saat halaman dibuka
  useEffect(() => {
    // Hanya render sekali saat semua data sudah siap
    if (hasRenderedInitialPreview.current) {
      return;
    }

    // Pastikan loading selesai, documentHtml sudah ada, dan variables sudah dimuat
    if (loading || !documentHtml || variables.length === 0 || !hasLoadedInitialData.current) {
      return;
    }

    // Tunggu sebentar untuk memastikan form values sudah di-set
    const timer = setTimeout(() => {
      // Ambil semua nilai dari form
      const allFormValues = getValues();

      // Create variables map dari form values
      const variablesMap: Record<string, string> = {};

      // Ambil semua variable dari form
      variables.forEach((v: any) => {
        const formKey = getVariableFormKey(v.name);
        const value = allFormValues[formKey] || v.value || '';
        if (value) {
          // Bersihkan variable name dari {} untuk digunakan sebagai key
          const cleanName = cleanVariableName(v.name);
          variablesMap[cleanName] = value;
          console.log(`📝 Auto-render variable: ${cleanName} = "${value}"`);
        }
      });

      // Tambahkan agreementNo ke variables map - SELALU tambahkan dengan semua variasi
      const agreementNoValue = allFormValues.agreementNo || '';
      if (agreementNoValue) {
        // Variable no_agreement
        variablesMap['no_agreement'] = agreementNoValue;
        variablesMap['NO_AGREEMENT'] = agreementNoValue;
        variablesMap['No_Agreement'] = agreementNoValue;
        variablesMap['No_agreement'] = agreementNoValue;

        // Variable no
        variablesMap['no'] = agreementNoValue;
        variablesMap['NO'] = agreementNoValue;
        variablesMap['No'] = agreementNoValue;

        console.log('✨ Auto-render: Added agreementNo to all variable formats');
      }

      console.log('🗺️ Auto-render: Final variablesMap keys:', Object.keys(variablesMap));

      // Replace variables dalam HTML string menggunakan fungsi utility
      try {
        const replacedHtml = replaceVariablesInHTML(documentHtml, variablesMap);
        setEditorContent(replacedHtml);
        hasRenderedInitialPreview.current = true;
        console.log('✅ Auto-render: Preview updated successfully with existing variables');
      } catch (err) {
        console.error('❌ Error auto-rendering document:', err);
        setEditorContent(documentHtml);
        hasRenderedInitialPreview.current = true;
      }
    }, 500); // Delay 500ms untuk memastikan form sudah di-reset

    return () => clearTimeout(timer);
  }, [loading, documentHtml, variables]);

  // Handle render document preview
  const handleRenderDocument = () => {
    if (!documentHtml) {
      setError('Dokumen belum dimuat. Silakan tunggu sebentar.');
      return;
    }

    // Ambil semua nilai akhir dari form saat tombol diklik
    const allFormValues = getValues();

    // Update preview dengan current form values
    const variablesMap: Record<string, string> = {};

    // Ambil semua variable dari form menggunakan nilai akhir
    variables.forEach((v: any) => {
      const formKey = getVariableFormKey(v.name);
      const value = allFormValues[formKey] || '';
      if (value) {
        // Bersihkan variable name dari {} untuk digunakan sebagai key
        const cleanName = cleanVariableName(v.name);
        variablesMap[cleanName] = value;
        console.log(`📝 Added variable from form: ${cleanName} = "${value}"`);
      }
    });

    // Tambahkan agreementNo ke variables map - SELALU tambahkan dengan semua variasi
    const agreementNoValue = allFormValues.agreementNo || '';
    console.log('📋 agreementNo value:', agreementNoValue);

    if (agreementNoValue) {
      // Variable no_agreement
      variablesMap['no_agreement'] = agreementNoValue;
      variablesMap['NO_AGREEMENT'] = agreementNoValue;
      variablesMap['No_Agreement'] = agreementNoValue;
      variablesMap['No_agreement'] = agreementNoValue;

      // Variable no
      variablesMap['no'] = agreementNoValue;
      variablesMap['NO'] = agreementNoValue;
      variablesMap['No'] = agreementNoValue;

      console.log('✨ Added agreementNo to all variable formats');
    } else {
      console.warn('⚠️ agreementNo is empty, checking if already in variablesMap...');
      // Jika agreementNo kosong, coba ambil dari variable 'no' yang sudah ada
      const noValue = variablesMap['no'] || variablesMap['NO'] || variablesMap['No'];
      if (noValue) {
        console.log('📌 Found "no" variable with value:', noValue);
        // Propagate ke semua format
        variablesMap['no_agreement'] = noValue;
        variablesMap['NO_AGREEMENT'] = noValue;
        variablesMap['No_Agreement'] = noValue;
        variablesMap['No_agreement'] = noValue;
        variablesMap['no'] = noValue;
        variablesMap['NO'] = noValue;
        variablesMap['No'] = noValue;
      }
    }

    console.log('🗺️ Final variablesMap keys:', Object.keys(variablesMap));
    console.log('🔍 NO_AGREEMENT in map:', 'NO_AGREEMENT' in variablesMap, variablesMap['NO_AGREEMENT']);
    console.log('🔍 NO in map:', 'NO' in variablesMap, variablesMap['NO']);
    console.log('🔍 no in map:', 'no' in variablesMap, variablesMap['no']);

    try {
      const replacedHtml = replaceVariablesInHTML(documentHtml, variablesMap);
      setEditorContent(replacedHtml);
      setError('');
      console.log('✅ Preview updated successfully');
    } catch (err) {
      console.error('❌ Error rendering document:', err);
      setError('Gagal merender dokumen');
    }
  };

  // Helper function to generate blob without saving (for AI comparison)
  const generateDocumentBlob = async (): Promise<Blob> => {
    if (!templateBlob || !template) {
      throw new Error('Template belum dimuat');
    }

    // Create variables map from form values
    const currentValues = getValues();
    const variablesMap: Record<string, string> = {};
    variables.forEach((v: any) => {
      const formKey = getVariableFormKey(v.name);
      const value = currentValues[formKey] || v.value || '';
      variablesMap[v.name] = value;
    });

    const agreementNoValue = currentValues.agreementNo || '';
    if (agreementNoValue) {
      variablesMap['no_agreement'] = agreementNoValue;
      variablesMap['NO_AGREEMENT'] = agreementNoValue;
      variablesMap['No_Agreement'] = agreementNoValue;
      variablesMap['No_agreement'] = agreementNoValue;
      variablesMap['no'] = agreementNoValue;
      variablesMap['NO'] = agreementNoValue;
      variablesMap['No'] = agreementNoValue;
    }

    const arrayBuffer = await templateBlob.arrayBuffer();
    const zip = new PizZip(arrayBuffer);
    const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });

    try {
      doc.render(variablesMap);
      let zipObj = doc.getZip();

      // Merge Attachments
      if (attachments && attachments.length > 0) {
        const attachmentImages = [];
        const token = localStorage.getItem('access_token');
        const headers: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};

        for (const att of attachments) {
          const imageUrl = typeof att.id === 'number'
            ? apiService.getAttachmentImageUrl(att.id)
            : att.imageUrl;

          if (!imageUrl) continue;

          try {
            const response = await fetch(imageUrl, { headers });
            if (response.ok) {
              const blob = await response.blob();
              attachmentImages.push({
                blob,
                name: `attachment_${att.id || att.order}`,
                type: blob.type,
                title: att.title
              });
            }
          } catch (e) {
            console.warn(`Failed to fetch attachment image: ${att.title}`, e);
          }
        }

        if (attachmentImages.length > 0) {
          zipObj = await appendAttachmentsToZip(zipObj, attachmentImages);
        }
      }

      return zipObj.generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        compression: 'DEFLATE',
      });
    } catch (error: any) {
      console.error('Docxtemplater error:', error);
      throw new Error(`Error rendering document: ${error.message}`);
    }
  };

  // Handle Compare with AI - Now handled by AICompareModal component

  const handleGenerateDocument = async () => {
    if (!templateBlob || !template) {
      setError('Template belum dimuat');
      return;
    }

    setGeneratingDoc(true);
    try {
      const blob = await generateDocumentBlob();

      // Upload as new version if task ID exists
      if (id) {
        try {
          const fileName = template.file_name || 'document.docx';
          const fileToUpload = new File([blob], fileName, {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          });

          console.log('Uploading generated document as new version...');
          await apiService.uploadTaskVersion(Number(id), fileToUpload);
          console.log('Version uploaded successfully');
        } catch (verErr) {
          console.error('Failed to upload version:', verErr);
          // Don't block download if version upload fails, but maybe log it
        }
      }

      const fileName = template.file_name || 'document.docx';
      saveAs(blob, fileName);
      setError('');
    } catch (err: any) {
      console.error('Error generating document:', err);
      setError(err.message || 'Gagal menghasilkan dokumen');
    } finally {
      setGeneratingDoc(false);
    }
  };

  // Handle form submit (update task instead of create)
  const onSubmit = async (data: any) => {
    if (!id) {
      setError('Task ID tidak ditemukan');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Prepare variables dari form data
      // Gunakan data dari form submission, dengan fallback ke getValues() untuk memastikan semua variable tersimpan
      const currentFormValues = getValues();
      const updatedVariables = variables.map((v: any) => {
        const formKey = getVariableFormKey(v.name);
        // Ambil nilai dari form data, jika tidak ada gunakan getValues(), jika tidak ada gunakan value default
        const value = data[formKey] || currentFormValues[formKey] || v.value || '';

        // Khusus untuk no_agreement, jika ada agreementNo dan variable no_agreement belum diisi, gunakan agreementNo
        const cleanName = cleanVariableName(v.name);
        if (cleanName === 'no_agreement' && !value && data.agreementNo) {
          return {
            name: v.name,
            position: v.position || 0,
            value: data.agreementNo,
            group_id: v.group_id || null
          };
        }

        return {
          name: v.name,
          position: v.position || 0,
          value: value,
          group_id: v.group_id || null
        };
      });

      // Prepare task update data
      const taskUpdateData = {
        title: data.title,
        description: data.description || null,
        agreement_no: data.agreementNo || null,
        category_id: Number(data.categoryId),
        agreement_type_id: Number(data.agreementTypeId),
        company_id: data.companyId ? Number(data.companyId) : null,
        template_id: template?.id || null,
        status_id: 2, // Set status to "Drafting" (2)
        variables: updatedVariables
      };

      // Update task
      const updatedTask = await apiService.updateTask(Number(id), taskUpdateData);

      console.log('Task updated successfully:', updatedTask);

      // Generate and upload document version automatically on save
      try {
        console.log('Generating document for versioning...');
        // Note: generateDocumentBlob uses current form values via getValues() internaly
        // but we should make sure it uses the updated values we just saved.
        // Since getValues() reads from form state which should be current, this is fine.

        const blob = await generateDocumentBlob();
        const fileName = template?.file_name || 'draft_version.docx';
        const fileToUpload = new File([blob], fileName, {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });

        console.log('Uploading draft as new version...');
        await apiService.uploadTaskVersion(Number(id), fileToUpload);
        console.log('Draft version uploaded successfully');
      } catch (verErr) {
        console.error('Failed to auto-version on save:', verErr);
        // We do not stop the flow here, just log the error
      }

      // Show success message and redirect
      alert('Draft berhasil disimpan! Status task diubah menjadi Drafting dan versi baru dokumen telah dibuat.');
      navigate(`/agreement/${id}`);
    } catch (err: any) {
      console.error('Error updating task:', err);
      setError(err.message || 'Gagal menyimpan draft. Silakan coba lagi.');
    } finally {
      setSaving(false);
    }
  };

  // Handle assignment form submit
  const handleAssignmentSubmit = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!id) {
      setError('Task ID tidak ditemukan');
      return;
    }

    // Assignment is optional, implemented separately
    // This can be added later if needed
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to convert month to Roman numeral
  const getMonthRoman = (month: number): string => {
    const romanNumerals: { [key: number]: string } = {
      1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI',
      7: 'VII', 8: 'VIII', 9: 'IX', 10: 'X', 11: 'XI', 12: 'XII'
    };
    return romanNumerals[month] || 'I';
  };

  // Function to generate agreement number
  const generateAgreementNumber = (sequenceNumber: number = 1): string => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    const monthRoman = getMonthRoman(month);

    const sequence = String(sequenceNumber).padStart(3, '0');
    const prefix = 'PKS-PSI';

    return `${sequence}/${prefix}/${monthRoman}/${year}`;
  };

  // Handle generate agreement number
  const handleGenerateAgreementNumber = () => {
    const generatedNumber = generateAgreementNumber(1);
    setValue('agreementNo', generatedNumber, { shouldValidate: true, shouldDirty: true });

    // Update no_agreement variable if exists
    const noAgreementVar = variables.find((v: any) => {
      const cleanName = cleanVariableName(v.name);
      return cleanName.toLowerCase() === 'no_agreement';
    });

    if (noAgreementVar) {
      const formKey = getVariableFormKey(noAgreementVar.name);
      setValue(formKey, generatedNumber, { shouldValidate: true, shouldDirty: true });
      console.log('Updated no_agreement variable:', formKey, generatedNumber);
    } else {
      console.log('No no_agreement variable found in variables list');
    }

    // Update variable 'no' if exists
    const noVar = variables.find((v: any) => {
      const cleanName = cleanVariableName(v.name);
      return cleanName.toLowerCase() === 'no';
    });

    if (noVar) {
      const formKey = getVariableFormKey(noVar.name);
      setValue(formKey, generatedNumber, { shouldValidate: true, shouldDirty: true });
      console.log('Updated no variable:', formKey, generatedNumber);
    } else {
      console.log('No no variable found in variables list');
    }

    // Preview tidak di-update otomatis - user harus klik "Update Document Preview" untuk melihat perubahan
  };

  // Handle attachment actions
  const handleAddAttachment = () => {
    const nextOrder = attachments.length + 1;
    setAttachmentForm({
      title: `Lampiran ${nextOrder}`,
      description: '',
      imageFile: null,
      imagePreview: ''
    });
    setEditingAttachment(null);
    setShowAttachmentModal(true);
  };

  const handleSaveAttachment = async (data: any) => {
    if (!id) return;

    try {
      if (editingAttachment && typeof editingAttachment.id === 'number') {
        // Update existing attachment
        const formData = new FormData();
        formData.append('title', data.title);
        formData.append('description', data.description || '');
        formData.append('order', String(editingAttachment.order));

        if (data.imageFile) {
          formData.append('file', data.imageFile);
        }

        await apiService.updateAttachment(editingAttachment.id as number, formData);

        // Reload attachments
        const attachmentsData = await apiService.getTaskAttachments(Number(id));
        const formattedAttachments: Attachment[] = attachmentsData.map((att: any) => ({
          id: att.id,
          title: att.title,
          imageUrl: apiService.getAttachmentImageUrl(att.id), // Use attachment ID
          description: att.description || '',
          order: att.order,
          file_path: att.file_path
        }));
        setAttachments(formattedAttachments);
      } else {
        // Add new attachment
        const formData = new FormData();
        formData.append('title', data.title);
        formData.append('description', data.description || '');
        formData.append('order', String(attachments.length));

        if (data.imageFile) {
          formData.append('file', data.imageFile);
        } else {
          throw new Error('Image file is required');
        }

        await apiService.createAttachment(Number(id), formData);

        // Reload attachments
        const attachmentsData = await apiService.getTaskAttachments(Number(id));
        const formattedAttachments: Attachment[] = attachmentsData.map((att: any) => ({
          id: att.id,
          title: att.title,
          imageUrl: apiService.getAttachmentImageUrl(att.id), // Use attachment ID
          description: att.description || '',
          order: att.order,
          file_path: att.file_path
        }));
        setAttachments(formattedAttachments);
      }

      setShowAttachmentModal(false);
    } catch (err: any) {
      console.error('Error saving attachment:', err);
      alert(err.message || 'Gagal menyimpan lampiran');
    }
  };

  const handleDeleteAttachment = async (attachmentId: string | number) => {
    if (!window.confirm('Hapus lampiran ini?')) return;

    try {
      if (typeof attachmentId === 'number') {
        await apiService.deleteAttachment(attachmentId);

        // Reload attachments
        const attachmentsData = await apiService.getTaskAttachments(Number(id!));
        const formattedAttachments: Attachment[] = attachmentsData.map((att: any) => ({
          id: att.id,
          title: att.title,
          imageUrl: apiService.getAttachmentUrl(att.file_path),
          description: att.description || '',
          order: att.order,
          file_path: att.file_path
        }));
        setAttachments(formattedAttachments);
      } else {
        // Local only (not yet saved)
        setAttachments(attachments.filter(att => att.id !== attachmentId));
      }
    } catch (err: any) {
      console.error('Error deleting attachment:', err);
      alert('Gagal menghapus lampiran');
    }
  };

  const handleDragStart = (index: number) => {
    setDraggingIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === index) return;

    const newAttachments = [...attachments];
    const draggedItem = newAttachments[draggingIndex];
    newAttachments.splice(draggingIndex, 1);
    newAttachments.splice(index, 0, draggedItem);

    const reorderedAttachments = newAttachments.map((att, idx) => ({ ...att, order: idx }));
    setAttachments(reorderedAttachments);
    setDraggingIndex(index);
  };

  const handleDragEnd = async () => {
    setDraggingIndex(null);

    // Save reorder to API if all attachments have numeric IDs
    if (id && attachments.every(att => typeof att.id === 'number')) {
      try {
        const orders = attachments.map(att => ({
          id: att.id as number,
          order: att.order
        }));
        await apiService.reorderAttachments(Number(id), orders);
      } catch (err: any) {
        console.error('Error reordering attachments:', err);
      }
    }
  };

  // Handle resize left panel
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      if (newWidth >= 300 && newWidth <= 800) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`border-b px-6 py-4 flex-shrink-0 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <button
              onClick={() => navigate(`/agreement/${id}`)}
              className="text-violet-600 hover:text-violet-700 flex items-center text-sm font-medium"
            >
              <i className="fa-solid fa-arrow-left mr-2"></i>
              Back to Agreement Detail
            </button>
            <div>
              <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Drafting V2: {task?.title}</h1>
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Edit variabel dan draft dokumen (Docxtemplater)</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => navigate(`/agreement/${id}`)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${darkMode
                ? 'text-gray-300 bg-gray-700 border border-gray-600 hover:bg-gray-600'
                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleGenerateDocument}
              disabled={generatingDoc || !templateBlob}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingDoc ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Generating...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-download mr-2"></i>
                  Generate Document
                </>
              )}
            </button>
            <button
              type="submit"
              form="drafting-form"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Menyimpan...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-save mr-2"></i>
                  Simpan Draft
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`px-6 py-3 border-b text-sm flex-shrink-0 transition-colors duration-300 ${darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
          }`}>
          {error}
        </div>
      )}

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden" style={{ height: 'calc(100vh - 120px)' }}>
        {/* Left Panel - Form & Variables */}
        <div
          className={`border-r overflow-y-auto flex flex-col relative transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          style={{ width: `${leftPanelWidth}px`, minWidth: '300px', maxWidth: '800px' }}
        >
          {/* Resize Handle */}
          <div
            className={`absolute right-0 top-0 bottom-0 w-1 hover:bg-violet-500 cursor-col-resize z-10 transition-colors ${darkMode ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsResizing(true);
            }}
          />

          <div className="p-6 space-y-6">
            {error && (
              <div className={`mb-6 px-4 py-3 border rounded-lg text-sm transition-colors duration-300 ${darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                {error}
              </div>
            )}

            <form id="drafting-form" className="space-y-8" onSubmit={handleSubmit(onSubmit)}>
              {/* Basic Information */}
              <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                <h2 className={`text-lg font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Basic Information</h2>

                <div className="space-y-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register('title')}
                      placeholder="Masukkan title agreement"
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                        ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                        : 'border-gray-300'
                        }`}
                    />
                    {errors.title && (
                      <p className={`text-xs mt-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                        {errors.title.message as string}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Description
                    </label>
                    <textarea
                      {...register('description')}
                      placeholder="Masukkan description agreement (opsional)"
                      rows={4}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none ${darkMode
                        ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                        : 'border-gray-300'
                        }`}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Category <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register('categoryId')}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                          ? 'bg-gray-900 border-gray-700 text-white'
                          : 'bg-gray-50 border-gray-300'
                          }`}
                        disabled
                      >
                        <option value="">-- Pilih Category --</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={String(cat.id)}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                      {errors.categoryId && (
                        <p className={`text-xs mt-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                          {errors.categoryId.message as string}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Agreement Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register('agreementTypeId')}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                          ? 'bg-gray-900 border-gray-700 text-white'
                          : 'bg-gray-50 border-gray-300'
                          }`}
                        disabled
                      >
                        <option value="">-- Pilih Agreement Type --</option>
                        {agreementTypes.map((type) => (
                          <option key={type.id} value={String(type.id)}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {errors.agreementTypeId && (
                        <p className={`text-xs mt-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                          {errors.agreementTypeId.message as string}
                        </p>
                      )}
                    </div>
                  </div>


                </div>
              </div>

              {/* No Agreement Section */}
              <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>No Agreement</h2>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Nomor Agreement
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      {...register('agreementNo')}
                      placeholder="001/PKS-PSI/I/2025"
                      className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${darkMode
                        ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                        : 'border-gray-300'
                        }`}
                    />
                    <button
                      type="button"
                      onClick={handleGenerateAgreementNumber}
                      className={`px-3 py-2 border rounded-lg text-xs font-medium transition-colors ${darkMode
                        ? 'bg-violet-600 text-white border-violet-600 hover:bg-violet-700'
                        : 'bg-violet-600 text-white border-violet-600 hover:bg-violet-700'
                        }`}
                    >
                      <i className="fa-solid fa-wand-magic-sparkles mr-1"></i>
                      Generate
                    </button>
                  </div>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Format: 001/PKS-PSI/I/2025 (001 = auto increment, PKS-PSI = default, I = bulan romawi, 2025 = tahun)
                  </p>
                </div>
              </div>

              {/* Variables Section */}
              {variables.length > 0 && (
                <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Variables</h2>
                    <button
                      type="button"
                      onClick={() => setVariablesExpanded(!variablesExpanded)}
                      className="text-sm text-violet-600 hover:text-violet-700"
                    >
                      {variablesExpanded ? (
                        <>
                          <i className="fa-solid fa-chevron-up mr-1"></i>Sembunyikan
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-chevron-down mr-1"></i>Tampilkan
                        </>
                      )}
                    </button>
                  </div>
                  {variablesExpanded && (
                    <>
                      <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Semua variables dari template. Variables yang harus Anda isi ditandai dengan badge "Required".
                      </p>

                      <div className="space-y-4">
                        {variables
                          .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
                          .map((variable: any) => {
                            const isForCurrentUser = isVariableForCurrentUser(variable);
                            const fieldName = getVariableFormKey(variable.name);

                            return (
                              <div key={`${variable.name}-${variable.position}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {variable.name}
                                    {isForCurrentUser && (
                                      <span className="ml-2 text-red-500">*</span>
                                    )}
                                  </label>
                                  {isForCurrentUser && (
                                    <span className={`px-2 py-1 text-xs font-medium rounded ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                                      }`}>
                                      Required
                                    </span>
                                  )}
                                </div>
                                <Controller
                                  name={fieldName}
                                  control={control}
                                  defaultValue={variable.value || ''}
                                  render={({ field }) => (
                                    <textarea
                                      {...field}
                                      placeholder={`Masukkan nilai untuk {${variable.name}}`}
                                      rows={3}
                                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-y ${isForCurrentUser
                                        ? darkMode
                                          ? 'border-gray-600 bg-gray-900 text-white placeholder-gray-500'
                                          : 'border-gray-300'
                                        : darkMode
                                          ? 'border-gray-700 bg-gray-900/50 text-gray-400'
                                          : 'border-gray-200 bg-gray-50'
                                        }`}
                                      disabled={!isForCurrentUser}
                                    />
                                  )}
                                />
                                {errors[fieldName] && (
                                  <p className={`text-xs mt-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                                    {errors[fieldName]?.message as string}
                                  </p>
                                )}
                                {!isForCurrentUser && variable.group_id && (
                                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Akan diisi oleh group lain
                                  </p>
                                )}
                              </div>
                            );
                          })}
                      </div>

                      {/* Render Document Button */}
                      <div className={`mt-6 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <button
                          type="button"
                          onClick={handleRenderDocument}
                          disabled={!documentHtml || !fileArrayBuffer}
                          className="w-full px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          <i className="fa-solid fa-refresh mr-2"></i>
                          Update Document Preview
                        </button>
                        <p className={`text-xs mt-2 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Klik tombol di atas untuk memperbarui preview dokumen dengan nilai variable yang sudah diisi
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Attachments Section */}
              <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Lampiran Dokumen
                  </h2>
                  <button
                    type="button"
                    onClick={handleAddAttachment}
                    className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors"
                  >
                    <i className="fa-solid fa-plus mr-2"></i>
                    Tambah Lampiran
                  </button>
                </div>

                {attachments.length === 0 ? (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <i className="fa-solid fa-file-image text-4xl mb-3"></i>
                    <p>Belum ada lampiran. Klik "Tambah Lampiran" untuk menambahkan.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {attachments.map((attachment, index) => (
                      <div
                        key={attachment.id}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-4 p-4 border rounded-lg cursor-move transition-colors ${darkMode
                          ? 'border-gray-700 bg-gray-900 hover:bg-gray-800'
                          : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                          } ${draggingIndex === index ? 'opacity-50' : ''}`}
                      >
                        <i className={`fa-solid fa-grip-vertical ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>

                        <AuthorizedImage
                          src={typeof attachment.id === 'number' ? apiService.getAttachmentImageUrl(attachment.id) : attachment.imageUrl}
                          alt={attachment.title}
                          className="w-20 h-20 object-cover rounded-lg border border-gray-300"
                        />

                        <div className="flex-1">
                          <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {attachment.title}
                          </h3>
                          <div
                            className={`text-sm mt-1 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                            dangerouslySetInnerHTML={{ __html: attachment.description || '<em>Tidak ada deskripsi</em>' }}
                          />
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingAttachment({
                                ...attachment,
                                imageUrl: typeof attachment.id === 'number'
                                  ? apiService.getAttachmentImageUrl(attachment.id)
                                  : attachment.imageUrl
                              });
                              setShowAttachmentModal(true);
                            }}
                            className={`p-2 rounded-lg transition-colors ${darkMode
                              ? 'hover:bg-gray-700 text-blue-400'
                              : 'hover:bg-blue-50 text-blue-600'
                              }`}
                            title="Edit"
                          >
                            <i className="fa-solid fa-pen"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            className={`p-2 rounded-lg transition-colors ${darkMode
                              ? 'hover:bg-gray-700 text-red-400'
                              : 'hover:bg-red-50 text-red-600'
                              }`}
                            title="Hapus"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                <div className="flex items-center justify-end space-x-4">
                  {/* <button
                    type="button"
                    onClick={() => navigate(`/agreement/${id}`)}
                    className={`px-6 py-2 border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 ${darkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    Cancel
                  </button> */}
                  <button
                    type="button"
                    onClick={() => setShowCompareModal(true)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
                  >
                    <i className="fa-solid fa-robot mr-2"></i>
                    Compare with AI
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-violet-500 flex items-center"
                  >
                    {saving ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        Menyimpan...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-save mr-2"></i>
                        Simpan Draft
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>

            {/* Attachment Modal */}
            <AttachmentModal
              isOpen={showAttachmentModal}
              onClose={() => setShowAttachmentModal(false)}
              onSave={handleSaveAttachment}
              initialData={editingAttachment}
            />

            {/* Compare with AI Modal */}
            <AICompareModal
              isOpen={showCompareModal}
              onClose={() => setShowCompareModal(false)}
              taskId={parseInt(id!)}
              darkMode={darkMode}
            />
          </div>
        </div>

        {/* Right Panel - Document Preview */}
        <div className={`flex-1 overflow-hidden flex flex-col transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'
          }`}>
          <div className={`px-6 py-4 border-b flex-shrink-0 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <h2 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Document Preview</h2>
            {template && (
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>File: {template.file_name}</p>
            )}
          </div>

          <div className="flex-1 overflow-hidden">
            {/* Hidden container for renderAsync to avoid React conflicts */}
            <div
              ref={hiddenRenderContainerRef}
              style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}
            />

            <div className={`h-full overflow-y-auto overflow-x-hidden transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-white'
              }`} style={{ paddingLeft: '40px', paddingRight: '40px' }}>
              {!fileArrayBuffer && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <i className={`fa-solid fa-file-word text-4xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                    <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Dokumen akan muncul setelah template dimuat</p>
                  </div>
                </div>
              )}
              {fileArrayBuffer && (editorContent || documentHtml) && (
                <>
                  <div
                    ref={previewContainerRef}
                    dangerouslySetInnerHTML={{ __html: editorContent || documentHtml }}
                    style={{
                      minHeight: 0,
                      width: '100%',
                      maxWidth: '100%'
                    }}
                  />

                  {/* Attachments Preview */}
                  {attachments.length > 0 && (
                    <div className="mt-12 space-y-12">
                      <div className={`border-t-2 pt-8 ${darkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                        <h2 className={`text-2xl font-bold text-center mb-8 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          LAMPIRAN
                        </h2>
                      </div>

                      {attachments.map((attachment, index) => (
                        <div key={attachment.id} className="page-break-before">
                          {/* Lampiran Title */}
                          <h3 className={`text-xl font-bold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {attachment.title}
                          </h3>

                          {/* Lampiran Image */}
                          <div className="flex justify-center mb-6">
                            <AuthorizedImage
                              src={typeof attachment.id === 'number' ? apiService.getAttachmentImageUrl(attachment.id) : attachment.imageUrl}
                              alt={attachment.title}
                              className="max-w-full h-auto border border-gray-300 rounded-lg"
                              style={{ maxHeight: '800px' }}
                            />
                          </div>

                          {/* Lampiran Description */}
                          {attachment.description && (
                            <div
                              className={`prose max-w-none ${darkMode ? 'prose-invert' : ''}`}
                              dangerouslySetInnerHTML={{ __html: attachment.description }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDraftingV2;
